import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import MaintenanceObjective from "@/common/components/MaintenanceObjective";
import ResumeItem from "@/common/components/ResumeItem";
import ReviewDescription from "@/common/components/ReviewDescription";
import { formatCurrency, formatDate, formatDateTime } from "@/common/utils/formatters";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import DirectiveEntryDiagnosticEntryPending from "../DiagnosticEntryPending";
import DiagnosticEntryObjectiveActivities from "./DiagnosticEntryObjectiveActivities";
import DiagnosticEntryObjectivePieces from "./DiagnosticEntryObjectivePieces";
import { MaintenanceForwardingDirectiveEntryStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";

const DirectiveEntryDiagnosticEntryCompleted = () => {
  const { forwarding } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry
  >();

  const queryClient = useQueryClient();

  const forwardingState = queryClient.getQueryState(['maintenanceForwarding', forwarding.id]);

  const directiveEntryObjectivesSummary = useMemo(() => {
    let piecesCount = 0;
    let activitiesCount = 0;

    const piecesTotalValue = forwarding.directive_entry.objectives.reduce((prev, curr) => {
      return prev + (curr.diagnostic_pieces?.reduce((prevB, currB) => {
        piecesCount += 1;

        return prevB + parseFloat(currB.total_value);
      }, 0) || 0);
    }, 0);

    const actitiviesTotalValue = forwarding.directive_entry.objectives.reduce((prev, curr) => {
      return prev + (curr.diagnostic_activities?.reduce((prevB, currB) => {
        activitiesCount += 1;

        return prevB + parseFloat(currB.total_value);
      }, 0) || 0);
    }, 0);

    let piecesCountDisplay: string;

    if (piecesCount === 0) piecesCountDisplay = 'Nenhuma peça'
    else if (piecesCount === 1) piecesCountDisplay = '1 Peça';
    else piecesCountDisplay = `${piecesCount} Peças`;

    let activitiesCountDisplay: string;

    if (activitiesCount === 0) activitiesCountDisplay = 'Nenhum serviço'
    else if (activitiesCount === 1) activitiesCountDisplay = '1 Serviço';
    else activitiesCountDisplay = `${activitiesCount} Serviços`;

    return {
      piecesCount,
      piecesCountDisplay,
      piecesTotalValue,
      activitiesCount,
      activitiesCountDisplay,
      actitiviesTotalValue,
    }
  }, [forwardingState?.dataUpdatedAt]);

  const isDiagnosticEntryDisapproved =
    forwarding?.directive_entry?.last_review?.state === 'disapproved' &&
    forwarding?.directive_entry.status === MaintenanceForwardingDirectiveEntryStatus.InProgress;

  const proposalDeadlineToCompleteDate = forwarding.establishment_choice_entry?.actual_proposal?.proposal_content?.estimated_completion_date 
    ? formatDate(new Date(forwarding.establishment_choice_entry?.actual_proposal?.proposal_content?.estimated_completion_date)) 
    : '--'

  return (
    <>
      <Box className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1.5">
          {isDiagnosticEntryDisapproved && (
            <ReviewDescription
              description={forwarding?.directive_entry?.last_review?.description}
            />
          )}

          <div className="flex gap-3">
            <ResumeItem
              label="Diagnóstico e orçamento enviado"
              value={
                forwarding?.directive_entry?.process_started_at
                  ? formatDateTime(new Date(forwarding?.directive_entry?.process_started_at))
                  : '--'
              }
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            {forwarding?.directive_entry?.completed_at && (
              <ResumeItem
                label="Aprovado em"
                value={formatDateTime(new Date(forwarding?.directive_entry?.completed_at))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <ResumeItem
            label="Parecer técnico"
            value={forwarding.directive_entry?.technical_response}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <ResumeItem
              label="Prazo para conclusão"
              value={proposalDeadlineToCompleteDate}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            <ResumeItem
              label="Total orçado em peças"
              value={`${directiveEntryObjectivesSummary.piecesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue || 0)}`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            <ResumeItem
              label="Total orçado em serviços"
              value={`${directiveEntryObjectivesSummary.activitiesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.actitiviesTotalValue || 0)}`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />

            <ResumeItem
              label="Valor total do orçamento"
              value={formatCurrency(parseFloat(forwarding.directive_entry.total_value) || 0)}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-base font-bold">
            Orçamento
          </p>

          {forwarding.directive_entry.objectives.map((objective) => {
            const piecesCount = objective.diagnostic_pieces?.length || 0
            const activitiesCount = objective.diagnostic_activities?.length || 0

            const piecesTotalValue =
              objective.diagnostic_pieces?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0
            const activitiesTotalValue =
              objective.diagnostic_activities?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0

            const piecesCountDisplay =
              piecesCount === 0 ? 'Nenhuma peça' : piecesCount === 1 ? '1 Peça' : `${piecesCount} Peças`
            const activitiesCountDisplay =
              activitiesCount === 0 ? 'Nenhum serviço' : activitiesCount === 1 ? '1 Serviço' : `${activitiesCount} Serviços`

            return (
              <Accordion
                key={objective.id}
                className="p-0 rounded-lg"
                triggerClassName="p-4"
                bodyClassName="p-4 pt-0"
                head={(
                  <div className="flex flex-1 items-center justify-between gap-4 min-w-0 pr-2">
                    <div className="min-w-0">
                      <MaintenanceObjective
                        systemDisplayName={objective.system.display_name}
                        actionDisplayName={objective.action.display_name}
                        partDisplayName={objective.part.display_name}
                        actionDescription={objective.action.description}
                      />
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                      <div className="flex items-center gap-0.5">
                        <span>{piecesCountDisplay}</span>

                        <span>•</span>

                        <span className="text-valid">{formatCurrency(piecesTotalValue)}</span>
                      </div>

                      <div className="flex items-center gap-0.5">
                        <span>{activitiesCountDisplay}</span>

                        <span>•</span>

                        <span className="text-valid">{formatCurrency(activitiesTotalValue)}</span>
                      </div>
                    </div>
                  </div>
                )}
                body={(
                  <div className="flex flex-col gap-3">
                    <DiagnosticEntryObjectivePieces
                      objective={objective}
                      dataUpdatedAt={forwardingState?.dataUpdatedAt}
                    />

                    <DiagnosticEntryObjectiveActivities
                      objective={objective}
                      dataUpdatedAt={forwardingState?.dataUpdatedAt}
                    />
                  </div>
                )}
              />
            )
          })}
        </div>
      </Box>

      <Box className="flex flex-col gap-4 p-4">
        <DirectiveEntryDiagnosticEntryPending />
      </Box>
    </>
  );
}

export default DirectiveEntryDiagnosticEntryCompleted;
